import React from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  Body,
  Link,
  SecondaryBody,
  TertiaryBody,
} from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import MuiTooltip from '@mui/material/Tooltip';
import { ScorecardIcons } from 'shared/components/scorecard_icons/scorecard_icons';
import type { IconType } from 'shared/components/scorecard_icons/base_icons';
import type {
  ScorecardRatingsSummary,
  ScorecardDetail,
} from 'talent_matching/types';
import NotesIcon from '@grnhse/seedling/lib/birch/icons/notes';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix(
  'talent_matching.panel.rediscovery_application_history'
);

type RatingConfig = {
  key: keyof ScorecardRatingsSummary;
  iconType: IconType;
  color: string;
};

const RATINGS: RatingConfig[] = [
  { key: 'strong_no', iconType: 'strong_no', color: FullPalette.POPPY_E200 },
  { key: 'no', iconType: 'no', color: FullPalette.POPPY_E50 },
  { key: 'yes', iconType: 'yes', color: FullPalette.GREEN_E50 },
  { key: 'strong_yes', iconType: 'strong_yes', color: FullPalette.GREEN_E200 },
  { key: 'no_decision', iconType: 'no_decision', color: FullPalette.GREY_500 },
];

const RATING_KEY_MAP: Record<string, keyof ScorecardRatingsSummary> = {
  strong_no: 'strong_no',
  no: 'no',
  yes: 'yes',
  strong_yes: 'strong_yes',
  no_decision: 'no_decision',
};

const Pill = styled.div<{ bgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Tokens.spacing['0.5']};
  padding: ${Tokens.spacing['1']};
  width: max-content;
  border-radius: 5px;
  background-color: ${({ bgColor }) => bgColor};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PillText = styled(TertiaryBody)`
  color: ${FullPalette.EVERGREEN_700};
  font-weight: 500;
  font-size: 0.8rem;
`;

const TooltipContent = styled.div`
  padding: ${Tokens.spacing['2']};
  min-width: 250px;
`;

type Props = {
  summary: ScorecardRatingsSummary;
  scorecardDetails?: ScorecardDetail[];
};

const ScorecardTooltipContent = ({
  ratingKey,
  iconType,
  count,
  scorecards,
}: {
  ratingKey: keyof ScorecardRatingsSummary;
  iconType: IconType;
  count: number;
  scorecards: ScorecardDetail[];
}) => {
  const filtered = scorecards.filter(
    (sc) => RATING_KEY_MAP[sc.rating] === ratingKey
  );

  return (
    <TooltipContent
      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ScorecardIcons type={iconType} className="small_icon" />
          <Body medium>{t(ratingKey, { count })}</Body>
        </Stack>
        {filtered.map((sc) => (
          <Stack key={sc.id} spacing={0.25}>
            <SecondaryBody medium>{sc.interviewerName}</SecondaryBody>
            <SecondaryBody metadata>{sc.interviewName}</SecondaryBody>
            <Link href={sc.scorecardUrl} target="_blank">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <NotesIcon size="sm" />
                <SecondaryBody>{t('scorecard_link')}</SecondaryBody>
              </Stack>
            </Link>
          </Stack>
        ))}
      </Stack>
    </TooltipContent>
  );
};

const tooltipSlotProps = {
  tooltip: {
    sx: {
      backgroundColor: FullPalette.WHITE,
      color: FullPalette.GREY_900,
      border: `1px solid ${FullPalette.GREY_300}`,
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
      maxWidth: 320,
    },
  },
  popper: {
    disablePortal: true,
  },
};

const ScorecardRatingPills = ({ summary, scorecardDetails = [] }: Props) => {
  return (
    <Stack direction="row" spacing={2}>
      {RATINGS.filter(({ key }) => summary[key] > 0).map(
        ({ key, iconType, color }) => (
          <MuiTooltip
            key={key}
            title={
              <ScorecardTooltipContent
                ratingKey={key}
                iconType={iconType}
                count={summary[key]}
                scorecards={scorecardDetails}
              />
            }
            placement="top"
            arrow
            slotProps={tooltipSlotProps}
          >
            <Pill bgColor={color}>
              <ScorecardIcons type={iconType} />
              <PillText>{t(key, { count: summary[key] })}</PillText>
            </Pill>
          </MuiTooltip>
        )
      )}
    </Stack>
  );
};

export default ScorecardRatingPills;
